/**
 * lib/responsive.ts
 * ──────────────────────────────────────────────────────────────────────────
 * THE single source of truth for responsive behaviour across web, Android and
 * iOS. No screen and no component may call `useWindowDimensions`,
 * `Dimensions.get` or compare a raw width anywhere else — they import from
 * here. That rule is what keeps twelve screens breaking at the same points.
 *
 * WHY THIS FILE IS LARGER THAN A BREAKPOINT CONSTANT
 * The design source is a desktop console: CSS Grid with `repeat(auto-fit,
 * minmax(210px, 1fr))` tracks and fixed `minmax(0, 1.7fr)` table columns.
 * React Native has no CSS Grid, so those two primitives are reimplemented
 * here as `autoFitColumns()` and `resolveTrackWidths()`. Every grid and every
 * table in the app is laid out by these two functions, which is why the
 * desktop rendering is pixel-identical to the reference and the phone
 * rendering degrades in one predictable way instead of twelve ad-hoc ones.
 *
 * API
 *   BP                  breakpoint edges
 *   useResponsive()     reactive layout state for components
 *   getViewport()       non-reactive snapshot for code outside render
 *   autoFitColumns()    CSS `repeat(auto-fit, minmax(min, 1fr))`
 *   resolveTrackWidths()CSS `minmax(0, Nfr)` track sizing for tables
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Dimensions, Platform, useWindowDimensions } from 'react-native';
import { chrome, contentWidth, pagePadding } from '../theme/spacing';

export const isWeb = Platform.OS === 'web';

/**
 * Breakpoint edges (px), measured on the WINDOW, not the content area.
 *
 *   mobile  : < 720   phones and small tablets in portrait
 *   tablet  : < 1024  large tablets, split-screen laptops
 *   desktop : < 1240  laptops — the design's own comfortable width
 *   wide    : >= 1240 the width the reference was drawn at
 *
 * 1240 is not arbitrary: it is the width below which the design itself hides
 * the header's "SUBLEDGER · NOT MONEY-OF-RECORD" pill.
 */
export const BP = { mobile: 720, tablet: 1024, desktop: 1240 } as const;

export type DeviceClass = 'mobile' | 'tablet' | 'desktop' | 'wide';

/** Pick a value per device class. `mobile` is required; wider classes fall back down. */
export interface SelectMap<T> {
  mobile: T;
  tablet?: T;
  desktop?: T;
  wide?: T;
}

function classify(width: number): DeviceClass {
  if (width < BP.mobile) return 'mobile';
  if (width < BP.tablet) return 'tablet';
  if (width < BP.desktop) return 'desktop';
  return 'wide';
}

function pick<T>(device: DeviceClass, map: SelectMap<T>): T {
  switch (device) {
    case 'wide':
      return map.wide ?? map.desktop ?? map.tablet ?? map.mobile;
    case 'desktop':
      return map.desktop ?? map.tablet ?? map.mobile;
    case 'tablet':
      return map.tablet ?? map.mobile;
    default:
      return map.mobile;
  }
}

export interface Responsive {
  width: number;
  height: number;
  device: DeviceClass;
  isMobile: boolean;
  isTablet: boolean;
  /** True at >= 1024: the docked sidebar, fixed drawer and real tables apply. */
  isDesktop: boolean;
  isWeb: boolean;
  /**
   * True when a table must degrade to stacked cards. Below this width a
   * six-column register cannot hold a readable line length, and the design's
   * own note — "no mobile layout exists" — means this is our decision to make:
   * one row becomes one card, same data, same order.
   */
  stackTables: boolean;
  /** True when the sidebar becomes an overlay drawer instead of a docked column. */
  overlayNav: boolean;
  /** True when the ledger drawer covers the viewport instead of a 520px panel. */
  fullScreenDrawer: boolean;
  /** Horizontal page padding for the current device. */
  pad: number;
  /** Vertical page padding (top / bottom) for the current device. */
  padTop: number;
  padBottom: number;
  select<T>(map: SelectMap<T>): T;
  /** Max content width for a named screen family, clamped to the viewport. */
  contentMax(kind?: keyof typeof contentWidth): number;
}

function build(width: number, height: number): Responsive {
  const device = classify(width);
  const isMobile = device === 'mobile';
  const isTablet = device === 'tablet';

  return {
    width,
    height,
    device,
    isMobile,
    isTablet,
    isDesktop: device === 'desktop' || device === 'wide',
    isWeb,
    stackTables: width < BP.tablet,
    overlayNav: width < BP.tablet,
    fullScreenDrawer: width < BP.mobile,
    pad: isMobile ? 16 : isTablet ? 22 : pagePadding.horizontal,
    padTop: isMobile ? 18 : pagePadding.top,
    padBottom: isMobile ? 32 : pagePadding.bottom,
    select: (map) => pick(device, map),
    contentMax: (kind = 'default') => Math.min(contentWidth[kind], Math.max(width, 320)),
  };
}

/** Reactive responsive state. Use inside components. */
export function useResponsive(): Responsive {
  const { width, height } = useWindowDimensions();
  return build(width, height);
}

/** Non-reactive snapshot, for code that runs outside a render pass. */
export function getViewport(): Responsive {
  const { width, height } = Dimensions.get('window');
  return build(width, height);
}

/* ═══════════════════════════════════════════════════════════════════════════
   CSS Grid, reimplemented
═══════════════════════════════════════════════════════════════════════════ */

/**
 * `repeat(auto-fit, minmax(min, 1fr))` — how many equal columns fit in
 * `available`, and how wide each one is once `gap` is removed.
 *
 * `itemCount` is what makes this auto-FIT rather than auto-FILL, and the
 * difference is visible: `auto-fill` keeps the empty tracks, so four KPI cards
 * in a 1162px row would occupy only 4/5 of it and stop short of the card below.
 * `auto-fit` COLLAPSES the tracks it does not need and the items stretch to
 * fill the row. Omit `itemCount` and you get auto-fill behaviour.
 *
 * Returns at least one column, so a 300px-minimum grid on a 320px phone
 * renders one full-width card rather than a zero-width one.
 */
export function autoFitColumns(
  available: number,
  min: number,
  gap: number,
  itemCount?: number,
): { count: number; width: number } {
  if (available <= 0) return { count: 1, width: min };
  const fits = Math.max(1, Math.floor((available + gap) / (min + gap)));
  // Collapse the tracks there is nothing to put in.
  const count = itemCount !== undefined ? Math.max(1, Math.min(fits, itemCount)) : fits;
  const width = (available - gap * (count - 1)) / count;
  return { count, width };
}

/**
 * A CSS Grid track, as the design writes them: `minmax(0, 1.7fr)` becomes
 * `{ fr: 1.7 }`, a fixed `100px` column becomes `{ px: 100 }`.
 *
 * The `minmax(0, …)` part is load-bearing and the design handoff calls it out
 * explicitly: a bare `fr` track is floored by its content, so a long holder
 * name widens one column in the body and not in the header, and the two grids
 * silently desynchronise. Modelling `fr` as a pure ratio of the LEFTOVER space
 * — never of content — is what reproduces `minmax(0, …)` here.
 */
export interface Track {
  fr?: number;
  px?: number;
}

/**
 * Resolve a track list to concrete pixel widths for a given container.
 * Fixed (`px`) tracks are subtracted first; whatever remains is divided among
 * the `fr` tracks in proportion to their ratios. The result is fed to both the
 * header row and every body row, so they cannot drift apart.
 */
export function resolveTrackWidths(
  tracks: readonly Track[],
  available: number,
  gap: number,
): number[] {
  if (tracks.length === 0) return [];
  const gaps = gap * (tracks.length - 1);
  const fixed = tracks.reduce((sum, t) => sum + (t.px ?? 0), 0);
  const totalFr = tracks.reduce((sum, t) => sum + (t.fr ?? 0), 0);
  // Never negative: a very narrow container collapses fr tracks to 0 rather
  // than producing NaN widths that RN renders as full-width.
  const free = Math.max(0, available - gaps - fixed);
  return tracks.map((t) => (t.px !== undefined ? t.px : totalFr > 0 ? (free * (t.fr ?? 0)) / totalFr : 0));
}

/** Width available to page content once the chrome is subtracted. */
export function contentAreaWidth(windowWidth: number, navCollapsed: boolean, overlay: boolean): number {
  if (overlay) return windowWidth;
  return windowWidth - (navCollapsed ? chrome.sidebarCollapsedWidth : chrome.sidebarWidth);
}
